import {
  FieldPath,
  FieldPathValue,
  useController,
  useFormContext,
} from 'react-hook-form';
import { OnboardingDraft } from '@intelligent-play-v2/domain';

/**
 * Bind a draft field that has no `<input>` of its own — a checkbox, a sport chip, a pasted
 * matrix, a list the reviewer adds rows to.
 *
 * react-hook-form only tracks names something has registered. Driving such a field with
 * `setValue` alone therefore looks like it works and quietly does nothing: the value never
 * lands in the form state, so the control never repaints and the edit never reaches the
 * committed draft. `useController` registers the name, which is what makes the toggle
 * toggle.
 */
export const useDraftField = <TName extends FieldPath<OnboardingDraft>>(
  name: TName
): [
  FieldPathValue<OnboardingDraft, TName>,
  (value: FieldPathValue<OnboardingDraft, TName>) => void
] => {
  const { control } = useFormContext<OnboardingDraft>();
  const { field } = useController({ control, name });
  // the hook widens the value to allow for unregistered names; ours are all in the draft
  return [
    field.value as FieldPathValue<OnboardingDraft, TName>,
    field.onChange,
  ];
};
